import { SimpleColumn } from "../types";
import type { ContractProposal } from "@/lib/types/contracts";
import {
  renderEmpty,
  renderCurrency,
  renderRelativeTime,
  renderTruncatedText,
  renderExternalLink,
} from "./shared";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import { FileText } from "lucide-react";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";

export function createContractColumns(_contracts: ContractProposal[] = []): SimpleColumn[] {
  const columns: SimpleColumn[] = [];

  columns.push({
    id: "title",
    label: "Contract",
    category: "basic",
    visible: true,
    priority: 1,
    width: 250,
    render: (_value: unknown, item: ContractProposal) => (
      <div className="min-w-0">
        <div className="font-medium text-card-foreground truncate">{item.title}</div>
        {item.description && (
          <div className="text-sm text-gray-500 truncate mt-1">{item.description}</div>
        )}
      </div>
    ),
  });

  columns.push({
    id: "status",
    label: "Status",
    category: "status",
    visible: true,
    priority: 2,
    width: 120,
  });

  columns.push({
    id: "amount",
    label: "Amount",
    category: "financial",
    visible: true,
    priority: 3,
    width: 120,
    render: (_value: unknown, item: ContractProposal) => renderCurrency(item.amount),
  });

  columns.push({
    id: "client_name",
    label: "Client",
    category: "contact",
    visible: true,
    priority: 4,
    width: 150,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.client_name) return renderEmpty();
      return (
        <SmartContactDisplay
          contactId={item.contact_id ?? ""}
          contactData={item.contact}
          fallbackText={item.client_name}
          showIcon={false}
          showPopover={false}
        />
      );
    },
  });

  columns.push({
    id: "description",
    label: "Description",
    category: "basic",
    visible: false,
    priority: 5,
    width: 200,
  });

  columns.push({
    id: "terms",
    label: "Terms",
    category: "basic",
    visible: false,
    priority: 6,
    width: 150,
  });

  columns.push({
    id: "currency",
    label: "Currency",
    category: "financial",
    visible: false,
    priority: 7,
    width: 80,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.currency) return <span className="text-gray-400">USD</span>;
      return <span className="font-mono text-gray-600 text-sm">{item.currency}</span>;
    },
  });

  columns.push({
    id: "contact_id",
    label: "Contact ID",
    category: "basic",
    visible: false,
    priority: 8,
    width: 120,
    render: (_value: unknown, item: ContractProposal) => (
      <SmartContactDisplay
        contactId={item.contact_id ?? ""}
        contactData={item.contact}
        fallbackText="-"
        showPopover={false}
      />
    ),
  });

  columns.push({
    id: "user_id",
    label: "User ID",
    category: "basic",
    visible: false,
    priority: 9,
    width: 120,
    render: (_value: unknown, item: ContractProposal) => (
      <SmartUserDisplay
        userId={item.user_id ?? ""}
        userData={item.user ?? undefined}
        fallbackText="-"
        showPopover={false}
      />
    ),
  });

  columns.push({
    id: "file_name",
    label: "File",
    category: "basic",
    visible: false,
    priority: 10,
    width: 200,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.file_name) return renderEmpty();
      return (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600 truncate" title={item.file_name}>
            {item.file_name}
          </span>
        </div>
      );
    },
  });

  columns.push({
    id: "file_size",
    label: "File Size",
    category: "basic",
    visible: false,
    priority: 11,
    width: 100,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.file_size) return renderEmpty();
      const sizeInMB = (item.file_size / (1024 * 1024)).toFixed(1);
      return <span className="text-sm text-gray-600">{sizeInMB} MB</span>;
    },
  });

  columns.push({
    id: "file_url",
    label: "File URL",
    category: "basic",
    visible: false,
    priority: 12,
    width: 200,
    render: (_value: unknown, item: ContractProposal) =>
      renderExternalLink(item.file_url ?? "", "View File"),
  });

  columns.push({
    id: "terms_and_conditions",
    label: "Terms & Conditions",
    category: "basic",
    visible: false,
    priority: 13,
    width: 200,
    render: (_value: unknown, item: ContractProposal) =>
      renderTruncatedText(item.terms_and_conditions),
  });

  columns.push({
    id: "notes",
    label: "Notes",
    category: "basic",
    visible: false,
    priority: 14,
    width: 200,
    render: (_value: unknown, item: ContractProposal) => renderTruncatedText(item.notes),
  });

  columns.push({
    id: "created_at",
    label: "Created",
    category: "dates",
    visible: true,
    priority: 15,
    width: 120,
    render: (value: string | null | undefined) => renderRelativeTime(value),
  });

  columns.push({
    id: "sent_at",
    label: "Sent",
    category: "dates",
    visible: true,
    priority: 16,
    width: 120,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.sent_at) return <span className="text-gray-400">Not sent</span>;
      return renderRelativeTime(item.sent_at);
    },
  });

  columns.push({
    id: "expires_at",
    label: "Expires",
    category: "dates",
    visible: true,
    priority: 17,
    width: 120,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.expires_at) return renderEmpty();

      const date = new Date(item.expires_at);
      const isExpired = date < new Date();

      return (
        <div className={`text-sm ${isExpired ? "text-red-600 font-medium" : "text-gray-600"}`}>
          {formatDate(item.expires_at)}
        </div>
      );
    },
  });

  columns.push({
    id: "viewed_at",
    label: "Viewed",
    category: "dates",
    visible: false,
    priority: 18,
    width: 120,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.viewed_at) return <span className="text-gray-400">Not viewed</span>;
      return renderRelativeTime(item.viewed_at);
    },
  });

  columns.push({
    id: "responded_at",
    label: "Responded",
    category: "dates",
    visible: false,
    priority: 19,
    width: 120,
    render: (_value: unknown, item: ContractProposal) => {
      if (!item.responded_at) return <span className="text-gray-400">No response</span>;
      return renderRelativeTime(item.responded_at);
    },
  });

  columns.push({
    id: "updated_at",
    label: "Updated",
    category: "dates",
    visible: false,
    priority: 20,
    width: 120,
    render: (value: string | null | undefined) => renderRelativeTime(value),
  });

  return columns.sort((a, b) => a.priority - b.priority);
}
